/**
 * Maps Bond Pulse activity rows to in-app navigation targets.
 */

const P2P_OFFER_PAGE_ACTIONS = new Set([
  "p2p_following_offer",
  "p2p_favorite_team_offer",
  "p2p_invite_accepted",
  "p2p_invite_rejected",
  "p2p_joined",
  "p2p_spots_added",
  "p2p_status_changed",
  "p2p_fixture_started",
  "p2p_creator_paid",
  "p2p_participant_paid",
  "p2p_invited_paid",
  "p2p_creator_refunded",
  "p2p_participant_refunded",
  "p2p_invited_refunded",
  "p2p_creator_unfilled_refunded",
  "p2p_cancelled",
  "p2p_created",
]);

const P2P_REQUESTS_PAGE_ACTIONS = new Set(["p2p_invite_received", "p2p_edited"]);

const BETHOUSE_HOUSE_PAGE_ACTIONS = new Set([
  "bethouse_following_live",
  "bethouse_paused",
  "bethouse_closed",
  "bethouse_fixture_suspended",
  "bethouse_fixture_unsuspended",
  "liability_limit_reached",
]);

const BETHOUSE_MANAGE_PAGE_ACTIONS = new Set([
  "bethouse_low_balance_warning",
  "betting_lost_settled",
  "bethouse_loan_requested",
  "bethouse_loan_interest_paid",
  "bethouse_loan_repaid",
  "bethouse_partner_invite_accepted",
  "bethouse_partner_invite_rejected",
  "bethouse_ticket_placed",
]);

const BETHOUSE_TICKET_PAGE_ACTIONS = new Set([
  "betting_won",
  "betting_lost",
  "betting_voided",
  "betting_won_paid",
  "betting_voided_refunded",
  "bethouse_fixture_kickoff",
]);

const BETHOUSE_REQUESTS_PAGE_ACTIONS = new Set([
  "bethouse_partner_invite_received",
]);

function resolveBetId(activity) {
  const metadata = activity?.metadata || {};
  const targetId = activity?.target?.id;
  if (metadata.betId) return String(metadata.betId);
  if (activity?.target?.type === "bethouse" && targetId) return String(targetId);
  if (activity?.target?.type === "bet" && targetId) return String(targetId);
  return null;
}

function resolveBettingId(activity) {
  const metadata = activity?.metadata || {};
  const targetId = activity?.target?.id;
  if (metadata.bettingId) return String(metadata.bettingId);
  if (activity?.target?.type === "betting" && targetId) return String(targetId);
  return null;
}

export function getActivityNavigationTarget(activity) {
  const action = String(activity?.action || "").toLowerCase();
  const metadata = activity?.metadata || {};

  if (P2P_OFFER_PAGE_ACTIONS.has(action)) {
    const p2pId = metadata.p2pId || activity?.target?.id;
    if (!p2pId) return null;
    return {
      pathname: "/app/p2p",
      search: `w=${encodeURIComponent(String(p2pId))}`,
    };
  }

  if (P2P_REQUESTS_PAGE_ACTIONS.has(action)) {
    const itemId =
      metadata.requestItemId ||
      (metadata.p2pId || activity?.target?.id
        ? `p2p-received-${metadata.p2pId || activity?.target?.id}`
        : null);
    if (!itemId) return null;
    return {
      pathname: `/app/requests/${encodeURIComponent(String(itemId))}`,
    };
  }

  if (BETHOUSE_HOUSE_PAGE_ACTIONS.has(action)) {
    const betId = resolveBetId(activity);
    if (!betId) return null;
    if (
      action === "bethouse_fixture_suspended" ||
      action === "bethouse_fixture_unsuspended"
    ) {
      return { pathname: "/app/joined-bets" };
    }
    return {
      pathname: `/app/bethouse/${encodeURIComponent(String(betId))}`,
    };
  }

  if (BETHOUSE_MANAGE_PAGE_ACTIONS.has(action)) {
    const betId = resolveBetId(activity);
    if (!betId) return null;
    if (action === "bethouse_ticket_placed") {
      return {
        pathname: `/app/bethouse/manage/${encodeURIComponent(String(betId))}/tickets`,
      };
    }
    return {
      pathname: `/app/bethouse/manage/${encodeURIComponent(String(betId))}`,
    };
  }

  if (BETHOUSE_TICKET_PAGE_ACTIONS.has(action)) {
    const bettingId = resolveBettingId(activity);
    if (!bettingId) return null;
    return {
      pathname: `/app/joined-bets/${encodeURIComponent(String(bettingId))}`,
    };
  }

  if (BETHOUSE_REQUESTS_PAGE_ACTIONS.has(action)) {
    const itemId =
      metadata.requestItemId ||
      (metadata.requestId
        ? `bet-received-${metadata.requestId}`
        : null);
    if (!itemId) return null;
    return {
      pathname: `/app/requests/${encodeURIComponent(String(itemId))}`,
    };
  }

  if (action === "prediction_following_create" || action === "prediction_favorite_team_create") {
    const fixtureId = metadata.fixtureId || metadata.fixtureSnapshot?._id;
    if (!fixtureId) return null;
    return {
      pathname: "/app/predictions",
      search: `f=${encodeURIComponent(String(fixtureId))}`,
    };
  }

  return null;
}

export function navigateFromActivity(navigate, activity) {
  const target = getActivityNavigationTarget(activity);
  if (!target?.pathname) return false;
  navigate({ pathname: target.pathname, search: target.search || "" });
  return true;
}

export function isFollowingP2PActivity(activity) {
  return isP2POfferActivity(activity);
}

export function isP2POfferActivity(activity) {
  const action = String(activity?.action || "").toLowerCase();
  return (
    P2P_OFFER_PAGE_ACTIONS.has(action) ||
    P2P_REQUESTS_PAGE_ACTIONS.has(action)
  );
}

export function isP2PLifecycleActivity(activity) {
  const action = String(activity?.action || "").toLowerCase();
  return (
    action === "p2p_invite_accepted" ||
    action === "p2p_invite_rejected" ||
    action === "p2p_joined" ||
    action === "p2p_status_changed" ||
    action === "p2p_fixture_started" ||
    action === "p2p_cancelled" ||
    action === "p2p_edited"
  );
}

export function isP2PSettlementActivity(activity) {
  const action = String(activity?.action || "").toLowerCase();
  return (
    action === "p2p_creator_paid" ||
    action === "p2p_participant_paid" ||
    action === "p2p_invited_paid" ||
    action === "p2p_creator_refunded" ||
    action === "p2p_participant_refunded" ||
    action === "p2p_invited_refunded"
  );
}

export function isFavoriteTeamP2PActivity(activity) {
  return String(activity?.action || "").toLowerCase() === "p2p_favorite_team_offer";
}

export function isP2PInviteActivity(activity) {
  return String(activity?.action || "").toLowerCase() === "p2p_invite_received";
}

export function isFollowingBetHouseLiveActivity(activity) {
  return BETHOUSE_HOUSE_PAGE_ACTIONS.has(
    String(activity?.action || "").toLowerCase(),
  );
}

export function isBetHouseTicketActivity(activity) {
  return BETHOUSE_TICKET_PAGE_ACTIONS.has(
    String(activity?.action || "").toLowerCase(),
  );
}

export function isBetHouseOperatorActivity(activity) {
  return BETHOUSE_MANAGE_PAGE_ACTIONS.has(
    String(activity?.action || "").toLowerCase(),
  );
}

export function isBetHousePartnerInviteActivity(activity) {
  return BETHOUSE_REQUESTS_PAGE_ACTIONS.has(
    String(activity?.action || "").toLowerCase(),
  );
}

export function isBetHouseActivity(activity) {
  const action = String(activity?.action || "").toLowerCase();
  return (
    BETHOUSE_HOUSE_PAGE_ACTIONS.has(action) ||
    BETHOUSE_MANAGE_PAGE_ACTIONS.has(action) ||
    BETHOUSE_TICKET_PAGE_ACTIONS.has(action) ||
    BETHOUSE_REQUESTS_PAGE_ACTIONS.has(action)
  );
}

export function isFollowingPredictionActivity(activity) {
  const action = String(activity?.action || "").toLowerCase();
  return (
    action === "prediction_following_create" ||
    action === "prediction_favorite_team_create"
  );
}

export function isFavoriteTeamPredictionActivity(activity) {
  return (
    String(activity?.action || "").toLowerCase() === "prediction_favorite_team_create"
  );
}
